/**
 * Relation picker (CC_11 Block 1, D-69) — the ONE shared picker for row/detail
 * views. Search engine over the column's target list (id-only contract,
 * D-64/D-65: the endpoint returns {row_id, display}, ACL-filtered) plus an
 * optional inline "Zeile erstellen" popup.
 *
 * 🔴 Depth cap = HARD 1: the create popup renders the target list's DATA
 * columns only — its relation fields are disabled with the hint "nach dem
 * Speichern verknüpfbar". No picker-in-picker, no create-on-the-fly inside a
 * relation (consistent with D-64: forms never create relation targets either).
 * Depth 2 is v1.6.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import { ryfeApi } from "../api";

import "./ryfe.scss";

import type { ListColumn } from "../api";

const SEARCH_DEBOUNCE_MS = 250;

interface Item {
  row_id: string;
  display: string;
}

export const RelationPicker = ({
  wsId,
  fileId,
  column,
  current,
  onSave,
  onClose,
}: {
  wsId: string;
  fileId: string;
  column: ListColumn;
  /** The row's current links for this column. */
  current: string[];
  onSave: (rowIds: string[]) => void | Promise<void>;
  onClose: () => void;
}) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState<Item[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set(current));
  const [creating, setCreating] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const single = (column.config?.cardinality as string) === "1:1";
  const targetListId =
    (column.config?.targetListId as string | undefined) || fileId;

  const search = useMemo(
    () => async (needle: string) => {
      try {
        const res = await ryfeApi.relationSearch(wsId, fileId, column.id, {
          q: needle,
          limit: 20,
        });
        setItems(res.items);
        setHasMore(res.hasMore);
      } catch {
        setItems([]);
        setHasMore(false);
      }
    },
    [wsId, fileId, column.id],
  );

  useEffect(() => {
    void search("");
  }, [search]);

  const onQuery = (value: string) => {
    setQ(value);
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => void search(value), SEARCH_DEBOUNCE_MS);
  };

  const toggle = (rowId: string) => {
    setSelected((prev) => {
      const next = new Set(single ? [] : prev);
      if (prev.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <h2 className="ryfe-panel__title">
          Verknüpfen · {column.name}
          {single ? " (1:1)" : ""}
        </h2>
        <input
          className="ryfe-field-input"
          placeholder="Suchen…"
          value={q}
          autoFocus
          data-testid="relpicker-search"
          onChange={(e) => onQuery(e.target.value)}
        />
        <ul className="ryfe-relpicker__list" data-testid="relpicker-results">
          {items.map((it) => (
            <li key={it.row_id}>
              <label className="ryfe-check">
                <input
                  type={single ? "radio" : "checkbox"}
                  checked={selected.has(it.row_id)}
                  onChange={() => toggle(it.row_id)}
                />
                {it.display}
              </label>
            </li>
          ))}
          {items.length === 0 && (
            <li className="ryfe-muted">
              Keine Treffer.
              {/* P18 (Mock Z2): der 0-Treffer-Fall betont das Anlegen — der
                  Suchbegriff belegt das Titelfeld des Create-Popups vor. */}
              {q.trim() !== "" && !creating && (
                <button
                  className="ryfe-btn ryfe-btn--ghost"
                  data-testid="relpicker-create-from-search"
                  onClick={() => setCreating(true)}
                >
                  ➕ Neue Zeile „{q.trim()}" erstellen
                </button>
              )}
            </li>
          )}
        </ul>
        {hasMore && (
          <p className="ryfe-muted" style={{ padding: 0 }}>
            Weitere Treffer — Suche verfeinern.
          </p>
        )}

        {creating ? (
          <RelationCreateRow
            wsId={wsId}
            targetListId={targetListId}
            prefillTitle={q.trim()}
            onCreated={(rowId, display) => {
              setItems((prev) => [{ row_id: rowId, display }, ...prev]);
              setSelected((prev) => {
                const next = new Set(single ? [] : prev);
                next.add(rowId);
                return next;
              });
              setCreating(false);
            }}
            onCancel={() => setCreating(false)}
          />
        ) : (
          <button
            className="ryfe-btn ryfe-btn--ghost"
            onClick={() => setCreating(true)}
          >
            ＋ Zeile erstellen…
          </button>
        )}

        <div className="ryfe-modal__actions">
          <span className="ryfe-topbar__spacer" />
          <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="ryfe-btn ryfe-btn--primary"
            onClick={() => void onSave(Array.from(selected))}
          >
            Übernehmen
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * The inline create popup (depth cap 1): data columns editable, relation
 * columns DISABLED with the "nach dem Speichern verknüpfbar" hint.
 */
const RelationCreateRow = ({
  wsId,
  targetListId,
  prefillTitle,
  onCreated,
  onCancel,
}: {
  wsId: string;
  targetListId: string;
  /** P18 (Mock Z2) — the search term pre-fills the target's title field. */
  prefillTitle?: string;
  onCreated: (rowId: string, display: string) => void;
  onCancel: () => void;
}) => {
  const [columns, setColumns] = useState<ListColumn[] | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    ryfeApi
      .getList(wsId, targetListId)
      .then((snap) => {
        if (!cancelled) {
          setColumns(snap.columns);
          const title = snap.columns.find((c) => c.type === "text");
          if (title && prefillTitle) {
            setValues((prev) =>
              prev[title.id] ? prev : { ...prev, [title.id]: prefillTitle },
            );
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setColumns([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [wsId, targetListId, prefillTitle]);

  if (!columns) {
    return <div className="ryfe-muted">Lädt…</div>;
  }

  const dataCols = columns.filter(
    (c) => c.type !== "relation" && c.type !== "uuid" && c.type !== "webhooks",
  );
  const relCols = columns.filter((c) => c.type === "relation");

  const create = async () => {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      const data: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(values)) {
        if (v !== "") {
          data[k] = v;
        }
      }
      const row = await ryfeApi.createListRow(wsId, targetListId, { data });
      const titleCol = columns.find((c) => c.type === "text");
      const display =
        (titleCol && String(row.data[titleCol.id] || "").trim()) || row.id;
      onCreated(row.id, display);
    } catch {
      /* row not created — the popup stays open */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ryfe-relpicker__create" data-testid="relpicker-create">
      <span className="ryfe-elmenu__head">NEUE ZEILE</span>
      {dataCols.map((c) => (
        <label key={c.id} className="ryfe-field-label">
          {c.name}
          <input
            className="ryfe-field-input"
            value={values[c.id] ?? ""}
            onChange={(e) =>
              setValues((prev) => ({ ...prev, [c.id]: e.target.value }))
            }
          />
        </label>
      ))}
      {relCols.map((c) => (
        <label key={c.id} className="ryfe-field-label">
          {c.name}
          {/* 🔴 Depth cap 1 (D-69): no picker-in-picker. */}
          <input
            className="ryfe-field-input"
            disabled
            data-testid={`relpicker-capped-${c.id}`}
            placeholder="nach dem Speichern verknüpfbar"
          />
        </label>
      ))}
      <div className="ryfe-modal__actions">
        <span className="ryfe-topbar__spacer" />
        <button className="ryfe-btn ryfe-btn--ghost" onClick={onCancel}>
          Abbrechen
        </button>
        <button
          className="ryfe-btn ryfe-btn--primary"
          disabled={busy}
          onClick={() => void create()}
        >
          {busy ? "Erstelle…" : "Erstellen"}
        </button>
      </div>
    </div>
  );
};
